import type { WeekDateRangeResponse } from '../api/generated';
import { getIsoYearAndWeekForDate } from '../utils/weekDateUtils';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PlanWeek {
  weekNumber: number;
  // Set when the week runs past the last week of the plan's own year.
  year?: number;
  startDate?: string;
}

// A plan's weeks are numbered in the shown year and may run past its last week,
// so week 54 of a 53-week year is week 1 of the next; a plan carried over from
// the previous year also has weeks 0 and below, week 0 being that year's last.
// `weeks` is the shown year's own calendar.
export function resolvePlanWeek(weekNumber: number, weeks: WeekDateRangeResponse[]): PlanWeek {
  if (weekNumber < 1 && weeks.length > 0) {
    const monday = new Date(Date.parse(weeks[0].startDate) + (weekNumber - 1) * 7 * DAY_MS);
    const isoWeek = getIsoYearAndWeekForDate(new Date(monday.getTime() + 3 * DAY_MS));
    return { weekNumber: isoWeek.week, year: isoWeek.year, startDate: monday.toISOString().slice(0, 10) };
  }
  const lastWeek = weeks.length > 0 ? weeks[weeks.length - 1] : undefined;
  if (!lastWeek || weekNumber <= weeks.length) {
    return { weekNumber, startDate: weeks.find((w) => w.weekNumber === weekNumber)?.startDate };
  }
  const monday = new Date(Date.parse(lastWeek.startDate) + (weekNumber - weeks.length) * 7 * DAY_MS);
  // An ISO week belongs to the year its Thursday falls in.
  const thursday = new Date(monday.getTime() + 3 * DAY_MS);
  return {
    weekNumber: weekNumber - weeks.length,
    year: thursday.getUTCFullYear(),
    startDate: monday.toISOString().slice(0, 10)
  };
}

export function formatPlanWeek(week: PlanWeek): string {
  return week.year === undefined ? String(week.weekNumber) : `${String(week.weekNumber)} (${String(week.year)})`;
}
